import { type PaletteMode, type ThemeOptions } from '@mui/material';
import type {} from '@mui/x-data-grid/themeAugmentation';
import type {} from '@mui/lab/themeAugmentation';
import { grey } from '@mui/material/colors';

// ── Palette tokens ────────────────────────────────────────────────────────────
//
// Light mode keeps the established navy + orange. Dark mode lifts the navy
// (#2f3659 is unreadable on a near-black bg) and warms the orange. Surface
// tokens are explicit so MUI doesn't fall back to its default `#121212` paper.

const LIGHT_PALETTE = {
  primary: { main: '#2f3659', dark: '#232942' },
  secondary: { main: '#f57f3c' },
  info: { main: '#0288d1' },
  background: { default: '#fafafa', paper: '#ffffff' },
} as const;

const DARK_PALETTE = {
  primary: { main: '#7c8cc8', dark: '#4f5d8e', light: '#a8b5da' },
  secondary: { main: '#ff9b5e' },
  info: { main: '#29b6f6' },
  background: { default: '#0f1117', paper: '#161922' },
  text: {
    primary: 'rgba(255,255,255,0.92)',
    secondary: 'rgba(255,255,255,0.60)',
    disabled: 'rgba(255,255,255,0.38)',
  },
  divider: 'rgba(255,255,255,0.08)',
  action: {
    hover: 'rgba(255,255,255,0.06)',
    selected: 'rgba(255,255,255,0.12)',
    disabled: 'rgba(255,255,255,0.30)',
    disabledBackground: 'rgba(255,255,255,0.12)',
  },
} as const;

// ── Typography scale ─────────────────────────────────────────────────────────
//
// `htmlFontSize: 14` shifts MUI's px-to-rem math so the px values below land at
// the size shown; matches the dense-information feel of a monitoring console
// without becoming cramped.

const TYPOGRAPHY = {
  fontFamily: '"Roboto","Helvetica","Arial",sans-serif',
  htmlFontSize: 14,
  fontSize: 14,
  h1: { fontSize: '2rem',   fontWeight: 700, lineHeight: 1.2,  letterSpacing: '-0.02em' },
  h2: { fontSize: '1.5rem', fontWeight: 700, lineHeight: 1.25, letterSpacing: '-0.01em' },
  h3: { fontSize: '1.25rem', fontWeight: 600, lineHeight: 1.3 },
  h4: { fontSize: '1.0625rem', fontWeight: 600, lineHeight: 1.35 },
  h5: { fontSize: '0.9375rem', fontWeight: 600, lineHeight: 1.4 },
  h6: { fontSize: '0.8125rem', fontWeight: 600, lineHeight: 1.4, letterSpacing: '0.02em' },
  subtitle1: { fontSize: '0.9375rem', fontWeight: 500, lineHeight: 1.5 },
  subtitle2: { fontSize: '0.8125rem', fontWeight: 500, lineHeight: 1.5 },
  body1: { fontSize: '0.875rem',  fontWeight: 400, lineHeight: 1.55 },
  body2: { fontSize: '0.8125rem', fontWeight: 400, lineHeight: 1.55 },
  button: { fontSize: '0.875rem', fontWeight: 600, textTransform: 'none' as const },
  caption: { fontSize: '0.75rem', fontWeight: 400, lineHeight: 1.4, letterSpacing: '0.02em' },
  overline: {
    fontSize: '0.6875rem', fontWeight: 600, lineHeight: 1.4,
    letterSpacing: '0.08em', textTransform: 'uppercase' as const,
  },
};

// ── Shadows ──────────────────────────────────────────────────────────────────
//
// MUI's default elevation ramp is tuned for light surfaces and creates grey
// halos on dark backgrounds. We define both ramps explicitly so Paper / Card
// elevations read correctly in both modes.

type Shadows = [
  'none', string, string, string, string, string, string, string,
  string, string, string, string, string, string, string, string,
  string, string, string, string, string, string, string, string, string,
];

const lightShadows: Shadows = [
  'none',
  '0 1px 2px rgba(15,17,23,0.04), 0 1px 3px rgba(15,17,23,0.06)',
  '0 1px 3px rgba(15,17,23,0.06), 0 2px 6px rgba(15,17,23,0.08)',
  '0 2px 4px rgba(15,17,23,0.06), 0 4px 12px rgba(15,17,23,0.08)',
  '0 4px 8px rgba(15,17,23,0.06), 0 8px 24px rgba(15,17,23,0.10)',
  ...(Array(20).fill('0 12px 24px rgba(15,17,23,0.12), 0 24px 48px rgba(15,17,23,0.16)') as string[]),
] as Shadows;

const darkShadows: Shadows = [
  'none',
  '0 1px 2px rgba(0,0,0,0.50), 0 2px 4px rgba(0,0,0,0.30)',
  '0 2px 6px rgba(0,0,0,0.55), 0 4px 12px rgba(0,0,0,0.35)',
  '0 4px 10px rgba(0,0,0,0.60), 0 8px 20px rgba(0,0,0,0.40)',
  '0 8px 16px rgba(0,0,0,0.65), 0 16px 32px rgba(0,0,0,0.45)',
  ...(Array(20).fill('0 16px 32px rgba(0,0,0,0.70), 0 24px 48px rgba(0,0,0,0.50)') as string[]),
] as Shadows;

export const getThemeOptions = (mode: PaletteMode): ThemeOptions => ({
  palette: {
    mode,
    ...(mode === 'light' ? LIGHT_PALETTE : DARK_PALETTE),
  },
  shape: { borderRadius: 6 },
  typography: TYPOGRAPHY,
  shadows: mode === 'light' ? lightShadows : darkShadows,
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          fontFeatureSettings: '"cv02","cv03","cv04"',
        },
      },
    },
    MuiAppBar: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          borderBottom: '1px solid',
          borderColor: mode === 'light' ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.08)',
        },
      },
    },
    MuiPaper: {
      defaultProps: { elevation: 1 },
      styleOverrides: {
        root: { backgroundImage: 'none' }, // kill the default dark-mode elevation overlay
      },
    },
    MuiButton: {
      styleOverrides: {
        root: { textTransform: 'none', fontWeight: 600, borderRadius: 6 },
        outlined: { borderColor: 'currentColor' },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: { textTransform: 'none', fontWeight: 600 },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: 4, fontWeight: 500 },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: { paddingTop: 10, paddingBottom: 10 },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          fontSize: 11,
          padding: '6px 8px',
          backgroundColor: mode === 'light' ? '#2f3659' : grey[800],
        },
        arrow: {
          color: mode === 'light' ? '#2f3659' : grey[800],
        },
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        ...(mode === 'light'
          ? {
              columnHeader: {
                backgroundColor: '#2f3659',
                color: 'white',
                fontWeight: 'bold',
              },
              iconButtonContainer: {
                '& .MuiButtonBase-root': {
                  color: 'white',
                },
              },
              menuIconButton: {
                color: 'white',
              },
            }
          : {
              columnHeader: {
                backgroundColor: '#1c2030',
                color: 'rgba(255,255,255,0.92)',
                fontWeight: 'bold',
              },
            }),
      },
    },
  },
});
